/*
 * Which brands the marketplace hero puts forward. Subscriptions are the pitch;
 * gift cards and game keys are the catalogue's depth, so the hero (its
 * "Top deals" panel, its "Up to N% off" and the brands it names) leaves out
 * these categories, as the Fleek deals connector does when browsing with no
 * category. A store with too few other brands (many smaller stores sell only
 * gift cards) gets them back, so the hero is never left empty.
 */
export const NON_SUBSCRIPTION_CATEGORIES = ["Gaming", "Gift Cards"];

export const isSubscriptionCategory = (category) =>
  !NON_SUBSCRIPTION_CATEGORIES.includes(String(category || "").trim());

const categoryOf = (row) => row?.service?.category ?? row?.category;

// Adult brands are sold in some stores but never put forward on the home page.
// The catalogue has no adult flag, so they are named here.
const ADULT_BRAND = /\b(stripchat|pornhub|onlyfans|brazzers|chaturbate|bongacams|livejasmin|xvideos|xhamster|camsoda|myfreecams)\b/i;

export const isAdultBrand = (row) => ADULT_BRAND.test(String(row?.service?.service_name ?? row?.name ?? ""));

/**
 * Up to ``n`` rows, keeping their order: the subscription brands first, then,
 * only if there are fewer than ``n`` of them, the gift cards and games.
 */
export const subscriptionsFirst = (rows, n = Infinity) => {
  const list = (rows || []).filter((r) => r && !isAdultBrand(r));
  const subs = list.filter((r) => isSubscriptionCategory(categoryOf(r)));
  if (subs.length >= n) return subs.slice(0, n);
  return [...subs, ...list.filter((r) => !isSubscriptionCategory(categoryOf(r)))].slice(0, n);
};

// "Discord Nitro" and "Discord", "Microsoft 365" and "Microsoft Office": one
// product family, named once.
export const brandFamily = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)[0]
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]/gu, "");

/**
 * The few brand names the hero gives as examples, from vendor_v2 rows in
 * display order: one per product family, subscriptions preferred.
 */
export const exampleBrandNames = (rows, n = 4) => {
  const seen = new Set();
  return subscriptionsFirst(rows)
    .filter((r) => {
      const key = brandFamily(r?.service?.service_name);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, n)
    .map((r) => r.service.service_name.trim());
};
